import React, { useState, useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckIcon } from '@heroicons/react/24/solid';
import { BookingContext } from '../context/BookingContext';
import { AuthContext } from '../context/AuthContext';
import { bookingAPI } from '../utils/api';
import { getBookedDatesForRoom, isDateBooked } from '../utils/bookingUtils';
import toast from '../utils/notifications';
import { dashboardRefreshEmitter } from '../utils/dashboardRefresh';
import DateSelect from '../components/DateSelect';

const Booking = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const room = params.get('room') || '1';

  // helper to format dates as YYYY-MM-DD for input value
  const toISODate = (d) => d.toISOString().slice(0, 10);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  
  // Calculate min date (today) and max date (365 days from today) for date inputs
  const minDate = toISODate(today);
  const maxDate = toISODate(new Date(today.getTime() + 365 * 24 * 60 * 60 * 1000));

  const { user } = useContext(AuthContext);
  
  // Helper function to split full name into first and last name
  const splitFullName = (fullName) => {
    if (!fullName) return { firstName: '', lastName: '' };
    const parts = fullName.trim().split(' ');
    if (parts.length === 1) return { firstName: parts[0], lastName: '' };
    const firstName = parts[0];
    const lastName = parts.slice(1).join(' ');
    return { firstName, lastName };
  };
  
  const getUserName = () => {
    // Only auto-fill for customers, not staff or admin
    if (!user || user.role === 'staff' || user.role === 'admin') {
      return { firstName: '', lastName: '' };
    }
    // Prioritize first_name and last_name from API
    if (user.first_name || user.last_name) {
      return {
        firstName: user.first_name || '',
        lastName: user.last_name || ''
      };
    }
    // Fallback to splitting full_name or name
    if (user.full_name) return splitFullName(user.full_name);
    if (user.name) return splitFullName(user.name);
    return { firstName: '', lastName: '' };
  };
  
  const initialUserName = getUserName();
  
  const [guest, setGuest] = useState({
    firstName: initialUserName.firstName,
    lastName: initialUserName.lastName,
    email: user?.email || '',
    phone: user?.phone || ''
  });
  const [checkIn, setCheckIn] = useState(params.get('checkIn') || toISODate(today));
  const [checkOut, setCheckOut] = useState(params.get('checkOut') || toISODate(tomorrow));
  const [guestCount, setGuestCount] = useState(1);
  const [extraBed, setExtraBed] = useState(false);
  const [specialRequests, setSpecialRequests] = useState('');
  const [loading, setLoading] = useState(false);
  const [bookedDates, setBookedDates] = useState([]);

  // Auto-fill user details when user logs in or component mounts (customers only)
  useEffect(() => {
    if (user && !guest.firstName && user.role === 'customer') {
      const userName = getUserName();
      setGuest({
        firstName: userName.firstName,
        lastName: userName.lastName,
        email: user.email || '',
        phone: user.phone || ''
      });
    }
  }, [user]);

  // Room prices: Room 1 and 2 are ₱2,500, Room 3 is ₱3,000
  const pricePerNight = (room === '1' || room === '2') ? 2500 : 3000;

  // Room guest capacity limits
  const roomCapacity = {
    '1': 2,  // Single Room: 2 guests max
    '2': 4,  // Double Room: 4 guests max
    '3': 4   // Deluxe Room: 4 guests max
  };
  const maxGuests = roomCapacity[room] || 4;

  // Fetch booked dates for this room on component mount
  useEffect(() => {
    const fetchBooked = async () => {
      // Map room number to room type
      const roomTypeMap = {
        '1': 'Single Room',
        '2': 'Double Room',
        '3': 'Deluxe Room'
      };
      const roomType = roomTypeMap[room] || 'Double Room';
      const dates = await getBookedDatesForRoom(roomType);
      setBookedDates(dates);
    };
    fetchBooked();
  }, [room]);

  const nights = Math.max(
    1,
    Math.round((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / (1000 * 60 * 60 * 24))
  );

  const days = nights + 0; // nights is already days stayed; keep as-is (you can define days differently if needed)

  const extraBedFee = 500;
  const roomTotal = pricePerNight * nights;
  const extraBedTotal = extraBed ? extraBedFee * nights : 0;
  const subtotal = (roomTotal + extraBedTotal).toFixed(2);

  const handleChange = (e) => setGuest((s) => ({ ...s, [e.target.name]: e.target.value }));

  const { addBooking } = useContext(BookingContext);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if user is logged in
    if (!user) {
      toast.error('Please login to make a booking');
      navigate('/login');
      return;
    }

    setLoading(true);

    try {
      // Map room number to room type
      const roomTypeMap = {
        '1': 'Single Room',
        '2': 'Double Room',
        '3': 'Deluxe Room'
      };

      const apiPayload = {
        room_type: roomTypeMap[room] || 'Double Room',
        check_in_date: checkIn, // YYYY-MM-DD format
        check_out_date: checkOut, // YYYY-MM-DD format
        number_of_guests: parseInt(guestCount),
        guest_name: `${guest.firstName} ${guest.lastName}`.trim(),
        guest_email: guest.email,
        guest_phone: guest.phone,
        number_of_rooms: 1,
        room_price_per_night: pricePerNight,
        add_extra_bed: extraBed,
        extra_bed_price: extraBed ? extraBedFee : null,
        special_requests: specialRequests || undefined
      };

      console.log('Sending booking to API:', apiPayload);
      console.log('Formatted dates:', {
        check_in_date: apiPayload.check_in_date,
        check_out_date: apiPayload.check_out_date,
        nights: nights
      });

      // Send to API
      const response = await bookingAPI.create(apiPayload);
      
      console.log('Booking API response:', response);

      // Also save to local context for immediate UI update
      const localPayload = {
        ...response.data.data,
        room,
        checkIn,
        checkOut,
        nights,
        pricePerNight,
        extraBed,
        extraBedFee: extraBed ? extraBedFee : 0,
        subtotal: Number(subtotal),
        guest,
        createdAt: new Date().toISOString()
      };

      if (addBooking) {
        addBooking(localPayload);
      }

      toast.success('Booking created successfully!');
      // Trigger dashboard refresh for admin
      dashboardRefreshEmitter.emit();
      navigate('/my-bookings');
    } catch (error) {
      console.error('Booking error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Validation errors:', error.response?.data?.errors);
      console.error('Full error object:', JSON.stringify(error.response?.data, null, 2));
      
      // Show specific validation errors if available
      if (error.response?.data?.errors) {
        const errorMessages = Object.entries(error.response.data.errors)
          .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
          .join('. ');
        toast.error(`Validation failed: ${errorMessages}`);
      } else if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to create booking. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  

  const formatDisplay = (iso) => {
    try {
      const d = new Date(iso + 'T00:00:00');
      return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    } catch (e) {
      return iso;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        {/* Main form */}
        <div className="lg:col-span-2 bg-white p-4 sm:p-6 rounded shadow">

          {/* Guest Info heading placed under the rules (only the heading & helper line) */}
          <div className="mb-4">
            <h2 className="text-xl sm:text-2xl font-semibold">Guest Info</h2>
            <p className="text-xs sm:text-sm text-gray-600">Guest names must match the valid ID which will be used at check-in.</p>
          </div>

          {/* Additional details: Overview, Amenities, Booking Rules (moved above guest form) */}
          <section className="mt-0 mb-6">
            <h3 className="text-lg sm:text-xl font-semibold mb-3">Overview</h3>
            <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6">
              {room === '1' && 'Simple, comfortable, and quiet space designed for restful nights. It\'s the perfect size for solo travelers or a pair seeking clean, convenient lodging. Relax in an intimate setting after a day of exploration.'}
              {room === '2' && 'More expansive layout, providing additional space to settle in and relax. It is ideal for small families or those planning a longer stay. Enjoy all the essential comforts in a clean and accessible setting.'}
              {room === '3' && 'Designed for the modern traveler, Room 3 provides reliable air-conditioning and a media setup for seamless relaxation. It\'s clean, functional, and equipped with the essentials to make your stay hassle-free.'}
            </p>

            <h4 className="text-base sm:text-lg font-semibold mb-3">Room Amenities</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-4 sm:mb-6">
              {[
                'Air Conditioning',
                'High‑Speed Wi‑Fi',
                'Electronic Safe'
              ].map((a) => (
                <div key={a} className="flex items-center space-x-2 sm:space-x-3 p-3 sm:p-2 border border-[#5d4134]/20 rounded bg-[#f4e7e1]/30">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center bg-white rounded shadow-sm flex-shrink-0">
                    <CheckIcon className="w-4 h-4 text-[#5d4134]" aria-hidden="true" />
                  </div>
                  <div className="text-xs sm:text-sm text-gray-700">{a}</div>
                </div>
              ))}
            </div>

            <h4 className="text-base sm:text-lg font-semibold mb-3">Booking Rules</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <div className="font-medium mb-2 text-sm sm:text-base">Check In</div>
                <ul className="list-none space-y-2 text-xs sm:text-sm text-gray-600">
                  <li className="flex items-start"><CheckIcon className="w-4 h-4 text-[#5d4134] mt-0.5 sm:mt-1 mr-2 flex-shrink-0" aria-hidden="true" /> Check-in from 14:00</li>
                  <li className="flex items-start"><CheckIcon className="w-4 h-4 text-[#5d4134] mt-0.5 sm:mt-1 mr-2 flex-shrink-0" aria-hidden="true" /> Please present valid ID at reception</li>
                  <li className="flex items-start"><CheckIcon className="w-4 h-4 text-[#5d4134] mt-0.5 sm:mt-1 mr-2 flex-shrink-0" aria-hidden="true" /> No smoking inside rooms</li>
                </ul>
              </div>

              <div>
                <div className="font-medium mb-2 text-sm sm:text-base">Check Out</div>
                <ul className="list-none space-y-2 text-xs sm:text-sm text-gray-600">
                  <li className="flex items-start"><CheckIcon className="w-4 h-4 text-[#5d4134] mt-0.5 sm:mt-1 mr-2 flex-shrink-0" aria-hidden="true" /> Check-out before 12:00</li>
                  <li className="flex items-start"><CheckIcon className="w-4 h-4 text-[#5d4134] mt-0.5 sm:mt-1 mr-2 flex-shrink-0" aria-hidden="true" /> Late check-out subject to availability</li>
                  <li className="flex items-start"><CheckIcon className="w-4 h-4 text-[#5d4134] mt-0.5 sm:mt-1 mr-2 flex-shrink-0" aria-hidden="true" /> Cancellation policy applies</li>
                </ul>
              </div>
            </div>
          </section>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs sm:text-sm text-gray-700 mb-1">First name *</label>
                <input name="firstName" value={guest.firstName} onChange={handleChange} className="w-full border rounded px-3 py-2.5 text-sm sm:text-base" placeholder="Your first name" required />
              </div>
              <div>
                <label className="block text-xs sm:text-sm text-gray-700 mb-1">Last name *</label>
                <input name="lastName" value={guest.lastName} onChange={handleChange} className="w-full border rounded px-3 py-2.5 text-sm sm:text-base" placeholder="Your last name" required />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs sm:text-sm text-gray-700 mb-1">Email *</label>
                <input name="email" type="email" value={guest.email} onChange={handleChange} className="w-full border rounded px-3 py-2.5 text-sm sm:text-base" placeholder="Email" required />
              </div>
              <div>
                <label className="block text-xs sm:text-sm text-gray-700 mb-1">Phone number *</label>
                <input name="phone" type="tel" value={guest.phone} onChange={handleChange} className="w-full border rounded px-3 py-2.5 text-sm sm:text-base" placeholder="(+63) Phone number" required />
              </div>
            </div>

            {/* Guest Count */}
            <div>
              <label className="block text-xs sm:text-sm text-gray-700 mb-1">Number of Guests *</label>
              <input 
                type="number" 
                min="1" 
                max={maxGuests}
                value={guestCount} 
                onChange={(e) => setGuestCount(Math.max(1, Math.min(maxGuests, parseInt(e.target.value) || 1)))}
                className="w-full border rounded px-3 py-2.5 text-sm sm:text-base" 
                placeholder="Number of guests"
                required 
              />
              <p className="text-xs text-gray-500 mt-1">Maximum {maxGuests} guest{maxGuests > 1 ? 's' : ''} for this room</p>
            </div>

            {/* Date pickers */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs sm:text-sm text-gray-700 mb-1">Check-in *</label>
                <DateSelect
                  value={checkIn}
                  onChange={(val) => {
                    const selectedDate = new Date(val + 'T00:00:00');
                    if (isDateBooked(selectedDate, bookedDates)) {
                      toast.error('This date is not available. Please choose another date.');
                      return;
                    }
                    setCheckIn(val);
                    const ci = new Date(val);
                    const minCo = new Date(ci);
                    minCo.setDate(ci.getDate() + 1);
                    if (new Date(checkOut) <= ci) setCheckOut(minCo.toISOString().slice(0,10));
                  }}
                  placeholder="Select check-in date"
                  min={minDate}
                  max={maxDate}
                  bookedDates={bookedDates}
                />
                {bookedDates.length > 0 && (
                  <p className="text-xs text-red-600 mt-1 font-medium">⚠ Dates with strikethrough are unavailable</p>
                )}
              </div>
              <div>
                <label className="block text-xs sm:text-sm text-gray-700 mb-1">Check-out *</label>
                <DateSelect
                  value={checkOut}
                  onChange={(val) => {
                    const selectedDate = new Date(val + 'T00:00:00');
                    if (isDateBooked(selectedDate, bookedDates)) {
                      toast.error('This date is not available. Please choose another date.');
                      return;
                    }
                    if (new Date(val) <= new Date(checkIn)) {
                      const ci = new Date(checkIn);
                      ci.setDate(ci.getDate() + 1);
                      setCheckOut(ci.toISOString().slice(0,10));
                    } else {
                      setCheckOut(val);
                    }
                  }}
                  placeholder="Select check-out date"
                  min={minDate}
                  max={maxDate}
                  bookedDates={bookedDates}
                />
              </div>
            </div>

            {/* Extra Bed Option */}
            <div className="border border-[#5d4134]/20 rounded p-4 bg-[#f4e7e1]/30">
              <label className="flex items-center space-x-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={extraBed} 
                  onChange={(e) => setExtraBed(e.target.checked)}
                  className="w-5 h-5 sm:w-4 sm:h-4 text-[#5d4134] border-gray-300 rounded focus:ring-[#5d4134] flex-shrink-0"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm sm:text-base text-gray-900">Add Extra Bed</div>
                  <div className="text-xs text-gray-600">₱500.00 per night</div>
                </div>
              </label>
            </div>

            <div>
              <label className="block text-xs sm:text-sm text-gray-700 mb-1">Special Requests (Optional)</label>
              <textarea 
                value={specialRequests}
                onChange={(e) => setSpecialRequests(e.target.value)}
                className="w-full border rounded px-3 py-2.5 h-24 text-sm sm:text-base" 
                placeholder="Any special requests" 
              />
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
              <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">{nights} night{nights > 1 ? 's' : ''} ({formatDisplay(checkIn)} — {formatDisplay(checkOut)})</div>
              <button 
                type="submit" 
                disabled={loading}
                className="bg-[#5d4134] hover:bg-[#4b3029] disabled:bg-gray-400 disabled:cursor-not-allowed text-[#f4e7e1] px-6 sm:px-8 py-3 sm:py-2 rounded font-semibold transition-colors text-sm sm:text-base w-full sm:w-auto"
              >
                {loading ? 'BOOKING...' : 'BOOK ROOM'}
              </button>
            </div>
          </form>
        </div>

        {/* Sidebar */}
        <aside className="bg-white p-4 sm:p-6 rounded shadow">
          <div className="flex items-start space-x-3 sm:space-x-4">
            <img src={`/images/room-${room}.png`} alt={`Room ${room}`} className="w-20 sm:w-24 h-16 sm:h-20 object-cover rounded flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-sm sm:text-base">Room {room}</h4>
              <div className="text-xs sm:text-sm text-gray-600">Executive Deluxe Room</div>
            </div>
          </div>

          <div className="mt-4 sm:mt-6">
            <div className="text-xs text-gray-500">Dates</div>
            <div className="mt-2 text-xs sm:text-sm">{formatDisplay(checkIn)} — {formatDisplay(checkOut)}</div>
            <div className="text-xs text-gray-500 mt-2">Duration</div>
            <div className="mt-1 text-xs sm:text-sm">{nights} night{nights > 1 ? 's' : ''} • {days} day{days > 1 ? 's' : ''}</div>
          </div>

          <div className="mt-4 sm:mt-6 border-t pt-4">
            <div className="flex justify-between text-xs sm:text-sm"><span>Room Price</span><span className="font-semibold">₱{pricePerNight.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
            <div className="mt-2 text-xs text-gray-500">{nights} x night{nights > 1 ? 's' : ''}</div>
            {extraBed && (
              <>
                <div className="flex justify-between text-xs sm:text-sm mt-3"><span>Extra Bed</span><span className="font-semibold">₱{extraBedFee.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                <div className="mt-1 text-xs text-gray-500">{nights} x night{nights > 1 ? 's' : ''}</div>
              </>
            )}
            <div className="flex justify-between items-center mt-3 pt-3 border-t">
              <div className="text-sm sm:text-base font-medium">Total</div>
              <div className="text-base sm:text-lg font-semibold">₱{Number(subtotal).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>
            
            
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Booking;
